/**
 * @fileoverview useMonthlyAllocations - Fetch and compute budget allocations for any month.
 *
 * Fetches template income transactions for a specific month and computes how much
 * has been allocated to each category from the budget template.
 *
 * @module hooks/useMonthlyAllocations
 */
import { useState, useEffect } from 'react';
import { transactionAPI } from '../services/api';

/**
 * Hook for fetching and computing monthly budget allocations.
 *
 * @param {string} month - Month in format "YYYY-MM"
 * @param {Object|null} budgetTemplate - Budget template with target amounts
 * @returns {Object} Allocation state
 * @returns {Object|null} return.allocations - Allocations by category
 * @returns {boolean} return.isLoading - Whether data is loading
 * @returns {Error|null} return.error - Error if fetch failed
 *
 * Allocations structure:
 * {
 *   "Groceries": { allocated: 500, remaining: 500 },
 *   "Gas": { allocated: 200, remaining: 0 },
 *   ...
 * }
 */
export const useMonthlyAllocations = (month, budgetTemplate) => {
    const [allocations, setAllocations] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!month || !budgetTemplate) {
            setAllocations(null);
            setIsLoading(false);
            return;
        }

        const fetchAllocations = async () => {
            try {
                setIsLoading(true);
                setError(null);

                // Fetch transactions for the month
                const transactions = await transactionAPI.getByMonth(month);

                // Filter for template income transactions
                const templateIncomeTransactions = transactions.filter(
                    transaction => transaction.type === 'Income' && transaction.usedTemplate
                );

                // Initialize allocations from template
                const monthlyAllocations = {};
                Object.keys(budgetTemplate).forEach(category => {
                    monthlyAllocations[category] = {
                        allocated: 0,
                        remaining: budgetTemplate[category].target
                    };
                });

                // Calculate allocated amounts from transactions
                templateIncomeTransactions.forEach(transaction => {
                    transaction.categories.forEach(cat => {
                        if (monthlyAllocations[cat.category]) {
                            monthlyAllocations[cat.category].allocated += cat.amount;
                            monthlyAllocations[cat.category].remaining = Math.max(0,
                                budgetTemplate[cat.category].target - monthlyAllocations[cat.category].allocated
                            );
                        }
                    });
                });

                setAllocations(monthlyAllocations);
            } catch (err) {
                setError(err);
                setAllocations(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllocations();
        // Use JSON.stringify for deep comparison to ensure re-fetch when template changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month, JSON.stringify(budgetTemplate)]);

    return { allocations, isLoading, error };
};
